<template>
  <div class="profile-page">
    <div class="profile-container">
      <div class="profile-header">
        <h1>👤 Oma Profiili</h1>
      </div>

      <div v-if="loading" class="loading-section">
        <div class="loading-spinner"></div>
        <span>Ladataan profiilia...</span>
      </div>

      <div v-else-if="error" class="error-section">
        <p class="error-message">{{ error }}</p>
        <button @click="loadProfile" class="retry-button">Yritä uudelleen</button>
      </div>

      <div v-else class="profile-content">
        <!-- Basic Info Section -->
        <div class="info-section">
          <h2>Perustiedot</h2>
          <div class="info-grid">
            <div class="info-item">
              <label>Raviremmi:</label>
              <span class="username">{{ profile.username }}</span>
            </div>
            <div class="info-item">
              <label>Sähköposti:</label>
              <span>{{ profile.email }}</span>
            </div>
            <div class="info-item">
              <label>Etunimi:</label>
              <span>{{ profile.first_name || 'Ei asetettu' }}</span>
            </div>
            <div class="info-item">
              <label>Sukunimi:</label>
              <span>{{ profile.last_name || 'Ei asetettu' }}</span>
            </div>
            <div class="info-item">
              <label>Tili:</label>
              <div class="account-status-container">
                <span class="account-status" :class="{ 'premium': profile.paid }">
                  {{ profile.paid ? 'Premium ⭐' : 'Perus' }}
                </span>
                <button 
                  v-if="!profile.paid" 
                  @click="confirmPremiumUpgrade" 
                  class="upgrade-button"
                  :disabled="isGeneratingInvoice"
                >
                  {{ isGeneratingInvoice ? '...' : 'Päivitä' }}
                </button>
              </div>
            </div>
            <div class="info-item" v-if="groupNames.length">
              <label>Ryhmät:</label>
              <div class="group-listing">
                <span v-for="groupName in groupNames" :key="groupName" class="group-chip">{{ groupName }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Game Stats Section -->
        <div class="stats-section">
          <h2>Pelitiedot</h2>
          <div class="stats-grid">
            <div class="stat-item">
              <span class="stat-label">Rahat:</span>
              <span class="stat-value">{{ formatMoney(profile.money) }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Vaihdot jäljellä:</span>
              <span class="stat-value">{{ profile.changes_left }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Pisteet yhteensä:</span>
              <span class="stat-value">{{ profile.total_points }}</span>
            </div>
          </div>
        </div>

        <!-- Danger Zone -->
        <div class="danger-section">
          <h2>Tilin hallinta</h2>
          <p class="danger-warning">
            ⚠️ Tilin poistaminen on pysyvä toiminto, jota ei voi peruuttaa.
          </p>
          <button @click="showDeleteModal = true" class="delete-button">
            🗑️ Poista Tili Pysyvästi
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Account Modal -->
    <Teleport to="body">
      <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
        <div class="modal-content delete-modal" @click.stop>
          <div class="modal-header">
            <h3>⚠️ Poista tili pysyvästi</h3>
            <button @click="closeDeleteModal" class="close-modal">&times;</button>
          </div>
          
          <div class="modal-body">
            <p><strong>Haluatko varmasti poistaa tilisi pysyvästi?</strong></p>
            
            <div class="deletion-consequences">
              <p>Tämä toiminto:</p>
              <ul>
                <li>Poistaa kaikki pelitietosi</li>
                <li>Poistaa remmisi ja valintasi</li>
                <li>Poistaa sinut ryhmistäsi (jos kuulut niihin)</li>
                <li><strong>EI VOI PERUUTTAA</strong></li>
              </ul>
            </div>
            
            <div class="confirmation-input">
              <label for="deleteConfirmation">
                Kirjoita <strong>"POISTA"</strong> vahvistaaksesi:
              </label>
              <input
                id="deleteConfirmation"
                v-model="deleteConfirmation"
                type="text"
                placeholder="POISTA"
                :disabled="deleting"
              />
            </div>
            
            <div v-if="deleteError" class="error-message">
              {{ deleteError }}
            </div>
          </div>
          
          <div class="modal-footer">
            <button @click="closeDeleteModal" class="cancel-button" :disabled="deleting">
              Peruuta
            </button>
            <button
              @click="deleteAccount"
              :disabled="deleteConfirmation !== 'POISTA' || deleting"
              class="confirm-delete-button"
            >
              {{ deleting ? 'Poistetaan...' : 'Poista Tili Pysyvästi' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Premium Upgrade Confirmation Modal -->
    <Teleport to="body">
      <div v-if="showPremiumUpgrade" class="modal-overlay" @click="cancelPremiumUpgrade">
        <div class="modal-content premium-modal" @click.stop>
          <button @click="cancelPremiumUpgrade" class="close-modal">&times;</button>
          <div class="premium-confirmation">
            <h3>Vahvista Premium-tilaus</h3>
            <div class="premium-details">
              <p><strong>Hinta:</strong> 3,20€</p>
              <p><strong>Toimitus:</strong> Premium-ominaisuudet aktivoituvat välittömästi</p>
              <p><strong>Maksu:</strong> Lasku lähetetään sähköpostiisi</p>
              <p><strong>Eräpäivä:</strong> 2 päivää laskun päivämäärästä</p>
            </div>
            
            <div class="payment-notice">
              <div class="notice-icon">ℹ️</div>
              <div class="notice-content">
                <p><strong>Tärkeää tietää:</strong></p>
                <ul>
                  <li>Käytä laskun viitenumeroa maksaessasi - muuten maksu ei kohdistu tilillesi</li>
                  <li>Jos et maksa laskua, ei hätää! Premium-oikeus vain poistuu automaattisesti</li>
                  <li>Emme lähetä muistutuksia tai peri lisäkuluja</li>
                </ul>
              </div>
            </div>
            
            <div class="premium-benefits-modal">
              <h4>🌟 Premium-version edut:</h4>
              <div class="benefits-grid">
                <div class="benefit-item">
                  <div class="benefit-icon">⏰</div>
                  <div class="benefit-text">
                    <strong>Koko jakso käytössä</strong>
                    <span>Ei 2 viikon rajoitusta</span>
                  </div>
                </div>
                <div class="benefit-item">
                  <div class="benefit-icon">🔄</div>
                  <div class="benefit-text">
                    <strong>15 vaihtoa</strong>
                    <span>Täysi joustavuus</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- EU Consumer Rights Compliance -->
            <div class="eu-compliance-section">
              <div class="checkbox-container">
                <input 
                  type="checkbox" 
                  id="cancelRightWaiver" 
                  v-model="cancelRightWaived"
                  :disabled="isGeneratingInvoice"
                />
                <label for="cancelRightWaiver" class="checkbox-label">
                  Hyväksyn, että suoritus alkaa välittömästi ja menetän 14 päivän peruuttamisoikeuden.
                </label>
              </div>
              
              <div class="checkbox-container">
                <input 
                  type="checkbox" 
                  id="termsAcceptance" 
                  v-model="termsAccepted"
                  :disabled="isGeneratingInvoice"
                />
                <label for="termsAcceptance" class="checkbox-label">
                  Hyväksyn 
                  <button @click="showTermsModal = true" class="terms-link-button" type="button">
                    käyttöehdot
                  </button>
                  (päivitetty 6.7.2025).
                </label>
              </div>
            </div>
            
            <div class="premium-actions">
              <button 
                @click="generateInvoice" 
                class="confirm-premium-btn"
                :disabled="isGeneratingInvoice || !cancelRightWaived || !termsAccepted"
              >
                {{ isGeneratingInvoice ? 'Luodaan laskua...' : 'Osta ja maksa 3,20€' }}
              </button>
              <button @click="cancelPremiumUpgrade" class="cancel-premium-btn">
                Peruuta
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Terms Modal -->
    <Teleport to="body">
      <div v-if="showTermsModal" class="modal-overlay" @click="showTermsModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h2>Käyttöehdot</h2>
            <button class="close-modal" @click="showTermsModal = false" aria-label="Sulje">&times;</button>
          </div>
          
          <div class="modal-body">
            <p>Käyttöehdot löydät kokonaisuudessaan <router-link to="/terms-of-service" @click="showTermsModal = false">täältä</router-link>.</p>
            <p>Viimeksi päivitetty: 6.7.2025</p>
          </div>
          
          <div class="modal-actions">
            <button class="btn btn-primary" @click="showTermsModal = false">Sulje</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Help Section -->
    <div class="profile-help-section">
      <div class="help-content">
        <div class="help-icon">💡</div>
        <div class="help-text">
          <p><strong>Vakava ongelma tilin kanssa?</strong></p>
          <p class="help-description">(Esim. et pääse kirjautumaan, tili lukittu)</p>
          <a href="mailto:info@raviremmi.com" class="help-email">info@raviremmi.com</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from '../plugins/axios';

export default {
  name: 'ProfilePage',
  setup() {
    const router = useRouter();
    
    // State
    const profile = ref({});
    const loading = ref(true);
    const error = ref('');
    
    // Premium upgrade
    const showPremiumUpgrade = ref(false);
    const isGeneratingInvoice = ref(false);
    const cancelRightWaived = ref(false);
    const termsAccepted = ref(false);
    const showTermsModal = ref(false);
    
    // Delete modal
    const showDeleteModal = ref(false);
    const deleteConfirmation = ref('');
    const deleting = ref(false);
    const deleteError = ref('');
    
    const groupNames = computed(() => {
      if (Array.isArray(profile.value?.Groups) && profile.value.Groups.length > 0) {
        return profile.value.Groups
          .map((group) => group?.GroupName)
          .filter((groupName) => typeof groupName === 'string' && groupName.length > 0);
      }
      if (profile.value?.GroupName) {
        return [profile.value.GroupName];
      }
      return [];
    });
    
    // Methods
    const loadProfile = async () => {
      loading.value = true;
      error.value = '';
      
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile`);
        profile.value = response.data;
      } catch (err) {
        console.error('Error loading profile:', err);
        error.value = err.response?.data?.message || 'Profiilin lataaminen epäonnistui.';
      } finally {
        loading.value = false;
      }
    };

    // Premium upgrade functions
    const confirmPremiumUpgrade = () => {
      showPremiumUpgrade.value = true;
    };

    const cancelPremiumUpgrade = () => {
      showPremiumUpgrade.value = false;
    };

    const generateInvoice = async () => {
      if (isGeneratingInvoice.value) return;
      
      isGeneratingInvoice.value = true;
      
      try {
        const response = await axios.post(`${process.env.VUE_APP_API_BASE_URL}/generate-invoice`);
        
        if (response.data && response.data.message) {
          alert(`✅ ${response.data.message}`);
        } else {
          alert('✅ Lasku lähetetty sähköpostiisi! Tarkista myös roskapostikansio.');
        }
      } catch (err) {
        console.error('Error generating invoice:', err);
        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Laskun luominen epäonnistui.';
        alert(`❌ ${errorMessage}`);
      } finally {
        isGeneratingInvoice.value = false;
        showPremiumUpgrade.value = false;
      }
    };
    
    const closeDeleteModal = () => {
      showDeleteModal.value = false;
      deleteConfirmation.value = '';
      deleteError.value = '';
    };
    
    const deleteAccount = async () => {
      if (deleteConfirmation.value !== 'POISTA') return;
      
      deleting.value = true;
      deleteError.value = '';
      
      try {
        await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/profile`, {
          data: { confirmation: 'POISTA' }
        });
        
        // Account deleted successfully - redirect to home and sign out
        alert('Tilisi on poistettu onnistuneesti. Sinut kirjataan nyt ulos.');
        
        // Sign out and redirect
        try {
          const { Auth } = await import('aws-amplify');
          await Auth.signOut();
        } catch (signOutError) {
          console.error('Error signing out:', signOutError);
        }
        
        router.push('/');
        
      } catch (err) {
        console.error('Error deleting account:', err);
        deleteError.value = err.response?.data?.message || 'Tilin poistaminen epäonnistui.';
      } finally {
        deleting.value = false;
      }
    };
    
    const formatMoney = (amount) => {
      if (typeof amount !== 'number') return '0';
      return amount.toLocaleString('fi-FI');
    };
    
    // Lifecycle
    onMounted(() => {
      loadProfile();
    });
    
    return {
      profile,
      loading,
      error,
      showPremiumUpgrade,
      isGeneratingInvoice,
      cancelRightWaived,
      termsAccepted,
      showTermsModal,
      showDeleteModal,
      deleteConfirmation,
      deleting,
      deleteError,
      loadProfile,
      confirmPremiumUpgrade,
      cancelPremiumUpgrade,
      generateInvoice,
      closeDeleteModal,
      deleteAccount,
      formatMoney
      ,
      groupNames
    };
  }
};
</script>

<style scoped>
.profile-page {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.profile-container {
  background-color: #112240;
  border-radius: 12px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  border: 1px solid #1e3a5f;
  overflow: hidden;
}

.profile-header {
  background: linear-gradient(135deg, rgba(10, 25, 47, 0.95) 0%, rgba(17, 34, 64, 0.95) 100%);
  padding: 30px;
  text-align: center;
  border-bottom: 1px solid #1e3a5f;
}

.profile-header h1 {
  color: #64ffda;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.loading-section, .error-section {
  padding: 40px;
  text-align: center;
  color: #a8b2d1;
}

.loading-spinner {
  border: 3px solid rgba(100, 255, 218, 0.3);
  border-radius: 50%;
  border-top: 3px solid #64ffda;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.retry-button {
  background-color: #64ffda;
  color: #0a192f;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 15px;
  transition: background-color 0.3s ease;
}

.retry-button:hover {
  background-color: #52d6c0;
}

.profile-content {
  padding: 0;
}

.info-section, .stats-section, .danger-section {
  padding: 30px;
  border-bottom: 1px solid #1e3a5f;
}

.danger-section {
  border-bottom: none;
}

.info-section h2, .stats-section h2, .danger-section h2 {
  color: #e6f1ff;
  font-size: 1.4rem;
  margin-bottom: 20px;
  font-weight: 600;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-item label {
  color: #8892b0;
  font-size: 0.9rem;
  font-weight: 500;
}

.info-item span {
  color: #ccd6f6;
  font-size: 1rem;
}

.group-listing {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.group-chip {
  display: inline-flex;
  align-items: center;
  background-color: rgba(100, 255, 218, 0.12);
  border: 1px solid rgba(100, 255, 218, 0.28);
  border-radius: 999px;
  color: #ccd6f6;
  padding: 6px 10px;
  font-size: 0.95rem;
}

.username {
  color: #64ffda !important;
  font-weight: 600;
}

.account-status-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.account-status.premium {
  color: #ffb86c !important;
  font-weight: 600;
}

.upgrade-button {
  background-color: #64ffda;
  color: #0a192f;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.upgrade-button:hover:not(:disabled) {
  background-color: #52d6c0;
  transform: translateY(-1px);
}

.upgrade-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-item {
  background-color: rgba(100, 255, 218, 0.05);
  padding: 20px;
  border-radius: 8px;
  border: 1px solid rgba(100, 255, 218, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-label {
  color: #8892b0;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.stat-value {
  color: #64ffda;
  font-size: 1.5rem;
  font-weight: 700;
}

.danger-section {
  background-color: rgba(255, 107, 107, 0.05);
  border-top: 1px solid rgba(255, 107, 107, 0.2);
}

.danger-warning {
  color: #ff6b6b;
  margin-bottom: 20px;
  font-size: 0.95rem;
}

.delete-button {
  background-color: #ff6b6b;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.delete-button:hover {
  background-color: #ff5252;
  transform: translateY(-1px);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background-color: #112240;
  border-radius: 12px;
  box-shadow: 0 20px 40px -15px rgba(2, 12, 27, 0.8);
  border: 1px solid #1e3a5f;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.delete-modal {
  border: 1px solid rgba(255, 107, 107, 0.3);
}

.modal-header {
  padding: 25px 30px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  color: #ff6b6b;
  font-size: 1.4rem;
  margin: 0;
}

.close-modal {
  background: none;
  border: none;
  color: #8892b0;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.3s ease;
}

.close-modal:hover {
  color: #64ffda;
}

.modal-body {
  padding: 20px 30px;
}

.modal-body p {
  color: #ccd6f6;
  margin-bottom: 20px;
}

.deletion-consequences {
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 25px;
}

.deletion-consequences p {
  color: #ff6b6b;
  font-weight: 600;
  margin-bottom: 10px;
}

.deletion-consequences ul {
  color: #ccd6f6;
  margin: 0;
  padding-left: 20px;
}

.deletion-consequences li {
  margin-bottom: 5px;
}

.confirmation-input {
  margin-bottom: 20px;
}

.confirmation-input label {
  display: block;
  color: #ccd6f6;
  margin-bottom: 10px;
  font-size: 0.95rem;
}

.confirmation-input input {
  width: 100%;
  background-color: #0a192f;
  border: 1px solid #1e3a5f;
  color: #ccd6f6;
  padding: 12px 15px;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.confirmation-input input:focus {
  outline: none;
  border-color: #ff6b6b;
  box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.15);
}

.modal-footer {
  padding: 0 30px 30px;
  display: flex;
  gap: 15px;
  justify-content: flex-end;
}

.cancel-button {
  background-color: transparent;
  color: #8892b0;
  border: 1px solid #1e3a5f;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.cancel-button:hover:not(:disabled) {
  color: #ccd6f6;
  border-color: #64ffda;
}

.confirm-delete-button {
  background-color: #ff6b6b;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.confirm-delete-button:hover:not(:disabled) {
  background-color: #ff5252;
}

.confirm-delete-button:disabled, .cancel-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  color: #ff6b6b;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  padding: 10px 15px;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .profile-page {
    padding: 10px;
  }
  
  .info-section, .stats-section, .danger-section {
    padding: 20px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .modal-content {
    margin: 10px;
  }
  
  .modal-header, .modal-body, .modal-footer {
    padding-left: 20px;
    padding-right: 20px;
  }
  
  .modal-footer {
    flex-direction: column;
  }
}

/* Premium Modal Styles */
.premium-modal {
  max-width: 500px;
  width: 90%;
}

.premium-confirmation h3 {
  color: #64ffda;
  margin-bottom: 20px;
  text-align: center;
}

.premium-details {
  margin-bottom: 25px;
}

.premium-details p {
  color: #ccd6f6;
  margin-bottom: 10px;
}

.premium-details ul {
  color: #a8b2d1;
  margin: 15px 0;
  padding-left: 20px;
}

.premium-details li {
  margin-bottom: 8px;
}

.price-info {
  color: #64ffda !important;
  font-size: 1.1rem;
  text-align: center;
  margin: 20px 0 !important;
}

.payment-info {
  color: #8892b0 !important;
  font-size: 0.9rem;
  text-align: center;
  font-style: italic;
}

.premium-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.confirm-premium-btn {
  background-color: #64ffda;
  color: #0a192f;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 180px;
}

.confirm-premium-btn:hover:not(:disabled) {
  background-color: #52d6c0;
  transform: translateY(-1px);
}

.confirm-premium-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancel-premium-btn {
  background-color: transparent;
  color: #8892b0;
  border: 1px solid #1e3a5f;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 120px;
}

.cancel-premium-btn:hover {
  color: #ccd6f6;
  border-color: #64ffda;
}

/* Profile Help Section */
.profile-help-section {
  margin-top: 2rem;
  padding: 1rem;
  background-color: rgba(100, 255, 218, 0.05);
  border: 1px solid rgba(100, 255, 218, 0.1);
  border-radius: 8px;
}

.help-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.help-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.help-text {
  flex: 1;
}

.help-text p {
  margin: 0 0 0.5rem 0;
  color: #e6f1ff;
}

.help-description {
  font-size: 0.9rem;
  color: #a8b2d1 !important;
  font-style: italic;
}

.help-email {
  color: #64ffda;
  text-decoration: none;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background-color: rgba(100, 255, 218, 0.1);
  border: 1px solid rgba(100, 255, 218, 0.2);
  transition: all 0.3s ease;
  display: inline-block;
  margin-top: 0.5rem;
}

.help-email:hover {
  background-color: rgba(100, 255, 218, 0.15);
  border-color: rgba(100, 255, 218, 0.4);
  transform: translateY(-1px);
}

@media (max-width: 480px) {
  .premium-actions {
    flex-direction: column;
  }
  
  .confirm-premium-btn, .cancel-premium-btn {
    min-width: auto;
  }
  
  .help-content {
    flex-direction: column;
    text-align: center;
  }
  
  .help-icon {
    align-self: center;
    margin-top: 0;
  }
}

/* EU Consumer Rights Compliance */
.eu-compliance-section {
  margin: 1.5rem 0;
  padding: 1rem;
  background-color: rgba(100, 255, 218, 0.05);
  border: 1px solid rgba(100, 255, 218, 0.1);
  border-radius: 8px;
}

.checkbox-container {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.checkbox-container:last-child {
  margin-bottom: 0;
}

.checkbox-container input[type="checkbox"] {
  margin-top: 0.25rem;
  flex-shrink: 0;
}

.checkbox-label {
  color: #ccd6f6;
  font-size: 0.9rem;
  line-height: 1.4;
  cursor: pointer;
}

.terms-link-button {
  background: none;
  border: none;
  color: #64ffda;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  padding: 0;
  margin: 0 2px;
  transition: color 0.3s ease;
}

.terms-link-button:hover {
  color: #52d6c0;
}

.confirm-premium-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Premium Benefits Grid */
.premium-benefits-modal {
  margin: 1.5rem 0;
  padding: 1rem;
  background-color: rgba(100, 255, 218, 0.08);
  border: 1px solid rgba(100, 255, 218, 0.2);
  border-radius: 8px;
}

.premium-benefits-modal h4 {
  color: #64ffda;
  font-size: 1.1rem;
  margin-bottom: 1rem;
  text-align: center;
}

.benefits-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: rgba(230, 241, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(100, 255, 218, 0.1);
  transition: all 0.3s ease;
}

.benefit-item:hover {
  background-color: rgba(100, 255, 218, 0.1);
  border-color: rgba(100, 255, 218, 0.3);
}

.benefit-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.benefit-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.benefit-text strong {
  color: #e6f1ff;
  font-size: 0.95rem;
  font-weight: 600;
}

.benefit-text span {
  color: #a8b2d1;
  font-size: 0.85rem;
}

@media (max-width: 480px) {
  .benefits-grid {
    grid-template-columns: 1fr;
  }
}

/* Payment Notice Styling */
.payment-notice {
  display: flex;
  gap: 1rem;
  background-color: rgba(100, 255, 218, 0.1);
  border-left: 4px solid #64ffda;
  padding: 1.25rem;
  border-radius: 8px;
  margin: 1rem 0;
}

.notice-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.notice-content p {
  margin-bottom: 0.75rem;
  color: #e6f1ff;
  font-weight: 600;
}

.notice-content ul {
  margin: 0;
  padding-left: 1.5rem;
  color: #a8b2d1;
}

.notice-content li {
  margin-bottom: 0.5rem;
  line-height: 1.4;
}
</style>
